import { endOfDay, format, startOfDay, subDays } from "date-fns";
import moment, { Moment } from "moment";
import { LightfieldsAnalyticsFilters } from "app/types/lightfieldsAnalytics";

export interface UtcDateRange {
  fromUtc: string;
  toUtc: string;
}

export interface LocalDateRange {
  fromDate: string;
  toDate: string;
}

type LocalDateInput = Date | Moment | string;

/** Normalize picker/state values to a local Date (UI stays in local time). */
export const toLocalDate = (value: LocalDateInput): Date => {
  if (moment.isMoment(value)) {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "string") {
    return moment(value, "YYYY-MM-DD").toDate();
  }

  return new Date(value);
};

/**
 * Convert a local calendar day to UTC ISO bounds for the API.
 * e.g. local 2026-06-29 00:00:00 → 2026-06-28T21:00:00.000Z (UTC+3)
 */
export const localDayBoundsToUtcIso = (localDate: LocalDateInput): UtcDateRange => {
  const date = toLocalDate(localDate);

  return {
    fromUtc: startOfDay(date).toISOString(),
    toUtc: endOfDay(date).toISOString(),
  };
};

/** Convert a local date range (inclusive days) to UTC ISO bounds for the API. */
export const localDateRangeBoundsToUtcIso = (
  fromLocal: LocalDateInput,
  toLocal: LocalDateInput,
): UtcDateRange => ({
  fromUtc: startOfDay(toLocalDate(fromLocal)).toISOString(),
  toUtc: endOfDay(toLocalDate(toLocal)).toISOString(),
});

export const getUtcRangeFromFilters = (
  filters: LightfieldsAnalyticsFilters,
): UtcDateRange => {
  if (filters.today) {
    return localDayBoundsToUtcIso(new Date());
  }

  if (filters.yesterday) {
    return localDayBoundsToUtcIso(subDays(new Date(), 1));
  }

  if (filters.fromDate && filters.toDate) {
    return localDateRangeBoundsToUtcIso(filters.fromDate, filters.toDate);
  }

  return localDayBoundsToUtcIso(new Date());
};

/** Local calendar dates as yyyy-MM-dd (used by deliveries API). */
export const getLocalDateRangeFromFilters = (
  filters: LightfieldsAnalyticsFilters,
): LocalDateRange => {
  if (filters.today) {
    const today = format(new Date(), "yyyy-MM-dd");
    return { fromDate: today, toDate: today };
  }

  if (filters.yesterday) {
    const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");
    return { fromDate: yesterday, toDate: yesterday };
  }

  if (filters.fromDate && filters.toDate) {
    return { fromDate: filters.fromDate, toDate: filters.toDate };
  }

  const today = format(new Date(), "yyyy-MM-dd");
  return { fromDate: today, toDate: today };
};

export const hasCompleteLightfieldsAnalyticsFilters = (
  filters: LightfieldsAnalyticsFilters,
): boolean => {
  if (filters.today || filters.yesterday) {
    return true;
  }

  if (filters.fromDate || filters.toDate) {
    return Boolean(filters.fromDate && filters.toDate);
  }

  return true;
};
